/**
 * AbstractCASigner - Abstract class for Remote CA signing
 *
 * Remote CA là loại ký số sử dụng dịch vụ CA từ xa.
 * Private key được lưu trên cloud, xác thực qua OTP/Mobile App.
 */

import { BaseSignatureProvider } from './ISignatureProvider';
import {
  SignType,
  SignProvider,
  SigningResult,
  SigningOptions,
  XmlSigningOptions,
  BatchSigningItem,
  BatchSigningResult,
  CertificateInfo,
} from '../types';

export interface CAServerConfig {
  /** URL của CA server */
  serverUrl: string;
  /** Client ID */
  clientId?: string;
  /** Client Secret */
  clientSecret?: string;
  /** Redirect URL sau khi xác thực */
  redirectUrl?: string;
  /** Timeout (ms) */
  timeout?: number;
}

/**
 * Trạng thái xác thực OTP
 */
export interface OTPVerificationState {
  transactionId: string;
  otpSent: boolean;
  otpExpiry?: Date;
  verified: boolean;
}

/**
 * Abstract CA Signer
 * Các concrete class (ViettelCASigner, VNPTCASigner, EasyCASigner, etc.) sẽ extend class này
 */
export abstract class AbstractCASigner extends BaseSignatureProvider {
  readonly signType = SignType.CA;
  abstract readonly signProvider: SignProvider;
  abstract readonly displayName: string;

  protected config: CAServerConfig;
  protected accessToken: string = '';
  protected otpState: OTPVerificationState | null = null;

  constructor(config?: Partial<CAServerConfig>) {
    super();
    this.config = {
      serverUrl: config?.serverUrl || '',
      clientId: config?.clientId,
      clientSecret: config?.clientSecret,
      redirectUrl: config?.redirectUrl,
      timeout: config?.timeout || 120000, // CA operations may take longer
    };
  }

  /**
   * Khởi tạo và xác thực với CA server
   */
  async initialize(): Promise<boolean> {
    try {
      const isHealthy = await this.checkServerHealth();
      if (!isHealthy) {
        console.error(`CA Server ${this.displayName} không phản hồi`);
        return false;
      }

      this._isReady = true;
      return true;
    } catch (error) {
      console.error('CA initialization failed:', error);
      return false;
    }
  }

  /**
   * Kiểm tra server health
   */
  async checkServerHealth(): Promise<boolean> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(`${this.config.serverUrl}/health`, {
        method: 'GET',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      return response.ok;
    } catch {
      return false;
    }
  }

  /**
   * Yêu cầu gửi OTP để xác thực ký số
   * @param phoneNumber - Số điện thoại nhận OTP
   */
  abstract requestOTP(phoneNumber: string): Promise<OTPVerificationState>;

  /**
   * Xác thực OTP
   * @param otp - Mã OTP người dùng nhập
   * @param transactionId - ID giao dịch từ requestOTP
   */
  abstract verifyOTP(otp: string, transactionId: string): Promise<boolean>;

  /**
   * Đăng nhập OAuth2 (nếu provider hỗ trợ)
   * @returns URL để redirect user đến trang đăng nhập
   */
  abstract getOAuthLoginUrl(): string;

  /**
   * Xử lý callback từ OAuth2 login
   * @param callbackUrl - URL callback với authorization code
   */
  abstract handleOAuthCallback(callbackUrl: string): Promise<boolean>;

  /**
   * Tạo request headers với access token
   */
  protected getAuthHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }

    return headers;
  }

  abstract getCertificateInfo(): Promise<CertificateInfo | null>;
  abstract signPdf(pdfData: string, options?: SigningOptions): Promise<SigningResult>;
  abstract signFile(fileData: string, fileName: string, options?: SigningOptions): Promise<SigningResult>;
  abstract signXml(xmlContent: string, options?: XmlSigningOptions): Promise<SigningResult>;

  /**
   * CA thường yêu cầu OTP cho mỗi lần ký batch
   */
  abstract signBatch(items: BatchSigningItem[], options?: SigningOptions): Promise<BatchSigningResult>;

  /**
   * Kiểm tra trạng thái OTP hiện tại
   */
  getOTPState(): OTPVerificationState | null {
    return this.otpState;
  }

  /**
   * Reset trạng thái OTP
   */
  resetOTPState(): void {
    this.otpState = null;
  }

  dispose(): void {
    this.accessToken = '';
    this.otpState = null;
    super.dispose();
  }
}
